import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../utils/supabase'
import { createImageVariants } from '../utils/imageProcessing'
import { PRODUCT_IMAGE_WIDTHS, getVariantPaths } from '../utils/productImages'
import { sortProducts } from '../utils/sortOrder'
import { saveSortOrder } from '../utils/saveSortOrder'

export const useSupabase = () => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      setLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null)
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  return {
    user,
    loading,
    signIn: async (email, password) => {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) throw error
      return { data, error }
    },
    signUp: async (email, password, firstName = '', lastName = '', phoneNumber = '') => {
      console.log('SignUp called with:', { email, firstName, lastName, phoneNumber }); // Debug log
      const { data, error } = await supabase.auth.signUp({ email, password })
      if (error) throw error

      // If signup successful and user exists, create record in custom users table
      if (data.user) {
        const insertData = {
          id: data.user.id,
          email: data.user.email,
          first_name: firstName,
          last_name: lastName,
          phone_number: phoneNumber,
          user_type: 'customer'
        };
        console.log('Inserting user data:', insertData); // Debug log
        
        const { error: userError } = await supabase.from('users').insert(insertData).select()

        if (userError) {
          console.error('User insert error:', userError); // Debug log
          // If user already exists, that's fine - don't throw error
          if (!userError.message.includes('duplicate key')) {
            console.warn('Could not create user record:', userError.message)
          }
        }
      }

      return { data, error }
    },
    signOut: () => supabase.auth.signOut(),
  }
}

// Hook for categories
export const useCategories = () => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchCategories()
  }, [])

  const fetchCategories = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase.from('categories').select('*').eq('active', true)
      if (error) throw error
      setCategories(data || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return {
    categories,
    loading,
    error,
    refetch: fetchCategories
  }
}

// Hook for products
const STORAGE_BUCKET = 'VanBelle'
const PUBLIC_BUCKET_MARKER = `/storage/v1/object/public/${STORAGE_BUCKET}/`

// Turn a public storage URL into its object path, e.g. "products/123-abc.jpg".
// Returns null for external URLs or files outside the products/ folder.
const getProductImagePath = (url) => {
  if (typeof url !== 'string') return null
  const index = url.indexOf(PUBLIC_BUCKET_MARKER)
  if (index === -1) return null
  const path = decodeURIComponent(url.slice(index + PUBLIC_BUCKET_MARKER.length).split('?')[0])
  return path.startsWith('products/') ? path : null
}

// Uploaded files never change (every upload gets a new name), so browsers and the CDN can keep them for a year
const IMMUTABLE_CACHE_SECONDS = '31536000'

// The product list is shared by every useProducts() caller (navbar, shop, category page),
// so navigating around the site reuses one request instead of refetching each time
const PRODUCTS_CACHE_TTL = 5 * 60 * 1000
let productsCache = { data: null, fetchedAt: 0, request: null }

const loadProducts = (force) => {
  const isFresh = productsCache.data && Date.now() - productsCache.fetchedAt < PRODUCTS_CACHE_TTL
  if (isFresh && !force) return Promise.resolve(productsCache.data)
  if (productsCache.request && !force) return productsCache.request

  const request = supabase.from('products').select('*, categories(*)').eq('active', true)
    .then(({ data, error }) => {
      if (error) throw error
      // Custom order from the admin (sort_order) first, then oldest first
      productsCache = { data: sortProducts(data || []), fetchedAt: Date.now(), request: null }
      return productsCache.data
    })
    .catch(err => {
      productsCache = { ...productsCache, request: null }
      throw err
    })
  productsCache = { ...productsCache, request }
  return request
}

// A product from the shared list, if it has been loaded (lets the product page skip a fetch)
export const getCachedProduct = (id) => productsCache.data?.find(p => String(p.id) === String(id)) || null

// `fresh: true` always refetches on mount (admin dashboard)
export const useProducts = ({ fresh = false } = {}) => {
  const [products, setProductsState] = useState(() => productsCache.data || [])
  const [loading, setLoading] = useState(() => !productsCache.data || fresh)
  const [error, setError] = useState(null)

  // Keep the shared cache in sync with local changes (add/edit/delete)
  const setProducts = useCallback((updater) => {
    setProductsState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      productsCache = { ...productsCache, data: next, fetchedAt: productsCache.fetchedAt || Date.now() }
      return next
    })
  }, [])

  const fetchProducts = useCallback(async (force = true) => {
    try {
      if (!productsCache.data || force) setLoading(true)
      setProductsState(await loadProducts(force))
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchProducts(fresh)
  }, [fetchProducts, fresh])

  const addProduct = async (product) => {
    try {
      const { data, error } = await supabase.from('products').insert(product).select()
      if (error) {
        // If RLS error, try with a simplified product (no image)
        if (error.message.includes('row-level security policy')) {
          console.warn('RLS policy blocked insertion, trying simplified product...');
          const simplifiedProduct = {
            name: product.name,
            description: product.description,
            price: product.price,
            active: true
          };
          const { data: simplifiedData, error: simplifiedError } = await supabase.from('products').insert(simplifiedProduct).select();
          if (simplifiedError) throw simplifiedError;
          return simplifiedData;
        }
        throw error;
      }
      setProducts(prev => sortProducts([...prev, ...data]))
      return data
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  const updateProduct = async (id, updates) => {
    try {
      const { data, error } = await supabase.from('products').update(updates).eq('id', id).select()
      if (error) throw error
      setProducts(prev => prev.map(p => p.id === id ? data[0] : p))
      return data
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  // Remove product images from storage. Only files in the products/ folder of our
  // bucket are touched, and URLs still used by another product are kept.
  // Failures are logged, not thrown, so they never block the product change itself.
  const deleteProductImages = async (urls = [], excludeProductId = null) => {
    try {
      const stillUsed = new Set(
        products
          .filter(p => p.id !== excludeProductId)
          .flatMap(p => [...(Array.isArray(p.images) ? p.images : []), p.image].filter(Boolean))
      )
      const mainPaths = [...new Set(urls)]
        .filter(url => !stillUsed.has(url))
        .map(getProductImagePath)
        .filter(Boolean)
      if (mainPaths.length === 0) return

      // Also remove the smaller size variants that belong to each image
      const paths = mainPaths.flatMap(getVariantPaths)
      const { data, error } = await supabase.storage.from(STORAGE_BUCKET).remove(paths)
      if (error) throw error
      // Storage returns the removed objects; a missing main file usually means RLS blocked the delete
      const removed = new Set((data || []).map(obj => obj.name))
      const notRemoved = mainPaths.filter(path => !removed.has(path))
      if (notRemoved.length > 0) {
        console.warn(`${notRemoved.length} of ${mainPaths.length} product images were not deleted from storage. Check the admin storage rules in secure-storage.sql.`)
      }
    } catch (err) {
      console.warn('Failed to delete product images from storage:', err.message)
    }
  }

  const deleteProduct = async (id) => {
    try {
      const product = products.find(p => p.id === id)
      const { error } = await supabase.from('products').delete().eq('id', id)
      if (error) throw error
      setProducts(prev => prev.filter(p => p.id !== id))
      if (product) {
        await deleteProductImages([...(Array.isArray(product.images) ? product.images : []), product.image].filter(Boolean), id)
      }
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  // Resize and compress the image into 1600/800/400px wide variants, upload them all,
  // and return the public URL of the largest one (the others are derived from its name)
  const uploadProductImage = async (file) => {
    const uploadedPaths = []
    try {
      const variants = await createImageVariants(file, PRODUCT_IMAGE_WIDTHS)
      const baseName = `${Date.now()}-${Math.random().toString(36).substring(2)}`
      const pathFor = (variant) => `products/${baseName}_w${variant.width}.${variant.ext}`

      await Promise.all(variants.map(async (variant) => {
        const path = pathFor(variant)
        const { error } = await supabase.storage.from(STORAGE_BUCKET).upload(path, variant.blob, {
          contentType: variant.type,
          cacheControl: IMMUTABLE_CACHE_SECONDS
        })

        if (error) {
          // If storage upload fails due to RLS, provide helpful error message
          if (error.message.includes('violates row-level security policy')) {
            throw new Error(`Storage permission denied. Product photos can only be uploaded by admins (see secure-storage.sql).`)
          }
          // If storage upload fails, throw a more user-friendly error
          throw new Error(`Storage upload failed: ${error.message}`)
        }
        uploadedPaths.push(path)
      }))

      const { data: { publicUrl } } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(pathFor(variants[0]))
      return publicUrl
    } catch (error) {
      // Don't leave a partial set of variants behind
      if (uploadedPaths.length > 0) {
        await supabase.storage.from(STORAGE_BUCKET).remove(uploadedPaths)
      }
      // Re-throw with a clearer message
      throw new Error(`Image upload failed: ${error.message}`)
    }
  }

  // Save the shop order (array of product ids, first shown first).
  // Pass null to go back to the default order. Restores the previous order if saving fails.
  const reorderProducts = async (orderedIds) => {
    const previous = products
    const position = new Map((orderedIds || []).map((id, index) => [id, index]))
    setProducts(sortProducts(products.map(p => ({
      ...p,
      sort_order: orderedIds ? (position.has(p.id) ? position.get(p.id) : p.sort_order) : null
    }))))
    try {
      if (orderedIds) await saveSortOrder('products', orderedIds)
      else await saveSortOrder('products', previous.map(p => p.id), { reset: true })
    } catch (err) {
      setProducts(previous)
      throw err
    }
  }

  return {
    products,
    loading,
    error,
    addProduct,
    reorderProducts,
    updateProduct,
    deleteProduct,
    uploadProductImage,
    deleteProductImages,
    refetch: fetchProducts
  }
}

// Hook for user addresses
export const useAddresses = (userId) => {
  const [addresses, setAddresses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchAddresses = useCallback(async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase.from('addresses').select('*').eq('user_id', userId)
      if (error) throw error
      setAddresses(data || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    if (userId) {
      fetchAddresses()
    }
  }, [userId, fetchAddresses])

  const addAddress = async (address) => {
    try {
      const { data, error } = await supabase.from('addresses').insert({ ...address, user_id: userId }).select()
      if (error) throw error
      setAddresses(prev => [...prev, ...data])
      return data
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  const updateAddress = async (id, updates) => {
    try {
      const { data, error } = await supabase.from('addresses').update(updates).eq('id', id).select()
      if (error) throw error
      setAddresses(prev => prev.map(a => a.id === id ? data[0] : a))
      return data
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  const deleteAddress = async (id) => {
    try {
      const { error } = await supabase.from('addresses').delete().eq('id', id)
      if (error) throw error
      setAddresses(prev => prev.filter(a => a.id !== id))
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  return {
    addresses,
    loading,
    error,
    addAddress,
    updateAddress,
    deleteAddress,
    refetch: fetchAddresses
  }
}

// Hook for orders
// `enabled: false` waits before fetching (e.g. until the profile's Orders tab is opened)
export const useOrders = (userId = null, { enabled = true } = {}) => {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // `{ silent: true }` refreshes without the loading state (live updates in the admin)
  const fetchOrders = useCallback(async (options) => {
    if (!enabled) return
    try {
      if (options?.silent !== true) setLoading(true)
      let query = supabase.from('orders').select('*')
      if (userId) {
        query = query.eq('user_id', userId)
      }
      const { data, error } = await query.order('created_at', { ascending: false })
      if (error) throw error
      setOrders(data || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [userId, enabled])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  const createOrder = async (orderData) => {
    try {
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      
      // For guest orders, use RPC function to bypass RLS issues
      if (!session) {
        console.log('Guest order - using RPC function');
        const params = {
          p_order_number: orderData.order_number,
          p_status: orderData.status,
          p_total_amount: orderData.total_amount,
          p_payment_method: orderData.payment_method,
          p_payment_status: orderData.payment_status,
          p_shipping_first_name: orderData.shipping_first_name,
          p_shipping_last_name: orderData.shipping_last_name,
          p_shipping_email: orderData.shipping_email,
          p_shipping_phone: orderData.shipping_phone,
          p_shipping_address_line_1: orderData.shipping_address_line_1,
          p_shipping_city: orderData.shipping_city,
          p_shipping_state_province: orderData.shipping_state_province,
          p_shipping_postal_code: orderData.shipping_postal_code,
          p_shipping_country: orderData.shipping_country,
          p_order_items: orderData.order_items,
          p_shipping_address_line_2: orderData.shipping_address_line_2 || null,
          p_notes: orderData.notes || null
        };
        // Only sent with a coupon, so checkout keeps working before add-coupons.sql is run
        if (orderData.coupon_code) params.p_coupon_code = orderData.coupon_code;
        const { data, error } = await supabase.rpc('insert_guest_order', params);
        
        if (error) throw error;
        
        setOrders(prev => [data, ...prev]);
        return data;
      }
      
      // For authenticated users, use regular insert
      const { data, error } = await supabase
        .from('orders')
        .insert(orderData)
        .select()
      
      if (error) throw error;
      
      setOrders(prev => [data[0], ...prev]);
      return data[0];
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  const updateOrder = async (id, updates) => {
    try {
      const { data, error } = await supabase.from('orders').update(updates).eq('id', id).select()
      if (error) throw error
      
      // Update the local state
      setOrders(prev => prev.map(order => 
        order.id === id ? { ...order, ...updates } : order
      ))
      
      return data[0]
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  return {
    orders,
    loading,
    error,
    createOrder,
    updateOrder,
    refetch: fetchOrders
  }
}

// Hook for users
export const useUsers = () => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase.from('users').select('*').order('created_at', { ascending: false })
      if (error) throw error
      setUsers(data || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const updateUser = async (id, updates) => {
    try {
      const { data, error } = await supabase.from('users').update(updates).eq('id', id).select()
      if (error) throw error
      setUsers(prev => prev.map(user => user.id === id ? data[0] : user))
      return data
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  return {
    users,
    loading,
    error,
    updateUser,
    refetch: fetchUsers
  }
}

// Hook for shipping cities
export const useShippingCities = () => {
  const [shippingCities, setShippingCities] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchShippingCities()
  }, [])

  const fetchShippingCities = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase.from('shipping_cities').select('*').order('city_name')
      if (error) throw error
      setShippingCities(data || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const addShippingCity = async (cityData) => {
    try {
      const { data, error } = await supabase.from('shipping_cities').insert(cityData).select()
      if (error) throw error
      setShippingCities(prev => [...prev, ...data])
      return data
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  const updateShippingCity = async (id, updates) => {
    try {
      const { data, error } = await supabase.from('shipping_cities').update(updates).eq('id', id).select()
      if (error) throw error
      setShippingCities(prev => prev.map(city => city.id === id ? data[0] : city))
      return data
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  const deleteShippingCity = async (id) => {
    try {
      const { error } = await supabase.from('shipping_cities').delete().eq('id', id)
      if (error) throw error
      setShippingCities(prev => prev.filter(city => city.id !== id))
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  return {
    shippingCities,
    loading,
    error,
    addShippingCity,
    updateShippingCity,
    deleteShippingCity,
    refetch: fetchShippingCities
  }
}